import { STAFF_ROLE_LABELS, STAFF_ROLE_ORDER, type StaffRole } from '@/data/staff-roles'
import type { TShowRoleAssignment } from '@schemas'
import {
  createStaffRosterMember,
  sortStaffRosterMembers,
  type StaffRosterMember,
} from './staff-roster'

export type StaffCertificateSigner = {
  name: string | null
  title: string
}

export type StaffCertificate = {
  id: string
  recipient: {
    name: string
    role: StaffRole
    roleLabel: string
  }
  signer: StaffCertificateSigner
  isPresidingJudge: boolean
}

type Participant = {
  assignment: TShowRoleAssignment
  member: StaffRosterMember
}

function groupParticipants(assignments: TShowRoleAssignment[]): Participant[] {
  const buckets = new Map<StaffRole, Participant[]>()

  assignments.forEach((assignment, index) => {
    const participant: Participant = {
      assignment,
      member: createStaffRosterMember(assignment, index),
    }

    const existing = buckets.get(assignment.role) ?? []
    existing.push(participant)
    buckets.set(assignment.role, existing)
  })

  const ordered: Participant[] = []
  for (const role of STAFF_ROLE_ORDER) {
    const group = buckets.get(role)
    if (!group || group.length === 0) continue

    const sortedMembers = sortStaffRosterMembers(group.map(item => item.member))
    const sortedParticipants = sortedMembers.map(member =>
      group.find(candidate => candidate.member.id === member.id)!
    )

    ordered.push(...sortedParticipants)
  }

  return ordered
}

function findOrganizer(participants: Participant[]): Participant | null {
  return participants.find(participant => participant.assignment.role === 'organizer') ?? null
}

function findPresidingJudge(participants: Participant[]): Participant | null {
  const judges = participants.filter(participant => participant.assignment.role === 'judge')
  if (judges.length === 0) {
    return null
  }

  const keywordMatch = judges.find(participant => {
    const contact = participant.assignment.contact?.toLocaleLowerCase()
    if (contact && contact.includes('presiding')) return true

    const name = participant.member.name.toLocaleLowerCase()
    return name.includes('presiding')
  })

  if (keywordMatch) {
    return keywordMatch
  }

  return judges[0]
}

function buildSigner(options: {
  participant: Participant
  organizer: Participant | null
  presidingJudge: Participant | null
}): StaffCertificateSigner {
  const { participant, organizer, presidingJudge } = options
  const isPresidingJudge =
    Boolean(presidingJudge?.member.id) && presidingJudge?.member.id === participant.member.id

  if (isPresidingJudge) {
    if (organizer) {
      return {
        name: organizer.member.name,
        title: 'Show Organizer',
      }
    }

    if (presidingJudge) {
      return {
        name: presidingJudge.member.name,
        title: 'Presiding Judge',
      }
    }

    return {
      name: null,
      title: 'Authorized Signer',
    }
  }

  if (presidingJudge) {
    return {
      name: presidingJudge.member.name,
      title: 'Presiding Judge',
    }
  }

  if (organizer) {
    return {
      name: organizer.member.name,
      title: 'Show Organizer',
    }
  }

  return {
    name: null,
    title: 'Authorized Signer',
  }
}

export function buildStaffCertificates(assignments: TShowRoleAssignment[]): StaffCertificate[] {
  if (!Array.isArray(assignments) || assignments.length === 0) {
    return []
  }

  const participants = groupParticipants(assignments)
  const organizer = findOrganizer(participants)
  const presidingJudge = findPresidingJudge(participants)

  return participants.map(participant => {
    const signer = buildSigner({ participant, organizer, presidingJudge })
    const isPresidingJudge =
      Boolean(presidingJudge?.member.id) && presidingJudge?.member.id === participant.member.id

    return {
      id: participant.member.id,
      recipient: {
        name: participant.member.name,
        role: participant.assignment.role,
        roleLabel: STAFF_ROLE_LABELS[participant.assignment.role].singular,
      },
      signer,
      isPresidingJudge,
    }
  })
}
